import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Star, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Seo } from '../components/Seo';
import { useSiteData } from '../hooks/useSiteData';
import { usePageContent } from '../hooks/usePageContent';
import { BUSINESS, SERVICE_AREAS } from '../lib/site';

const Contact = () => {
  const { data: settings } = useSiteData('settings');
  const { t } = usePageContent('contact');
  const phone = settings?.phone || BUSINESS.phone;
  const email = settings?.email || BUSINESS.email;
  const instagram = settings?.instagram || BUSINESS.instagram;
  const facebook = settings?.facebook || BUSINESS.facebook;
  const review = settings?.google_review || BUSINESS.googleReview;

  const channels = [
    { icon: Phone, label: 'Phone', value: phone, href: `tel:${phone.replace(/\s/g, '')}` },
    { icon: Mail, label: 'Email', value: email, href: `mailto:${email}` },
    { icon: Instagram, label: 'Instagram', value: '@mayden_films', href: instagram },
    { icon: Facebook, label: 'Facebook', value: 'Mayden Films', href: facebook },
  ];

  return (
    <div className="min-h-screen bg-[#1F1A17]">
      <Seo
        exactTitle="Contact | Mayden Films"
        description="Get in touch with Mayden Films — Glam Booth hire and event media across Cranbourne and Southeast Melbourne. Enquiries are normally answered within two business days."
        path="/contact"
      />

      <section className="pt-36 pb-16 bg-gradient-to-br from-[#1F1A17] via-[#2A2420] to-[#1F1A17]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EADBC8] mb-5">{t('hero_title', 'Contact Mayden Films')}</h1>
          <p className="text-base md:text-lg text-[#EADBC8]/70 max-w-2xl mx-auto">
            {t('hero_subtitle', 'Start with an enquiry by any channel below. Confirmed booking communication is handled by email.')}
          </p>
        </div>
      </section>

      <section className="py-20 bg-[#1A1612]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {channels.map((c) => (
                  <a key={c.label} href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                    className="bg-[#2A2420] border border-[#C5A880]/20 hover:border-[#C5A880] rounded-xl p-5 transition-colors group" data-testid={`contact-${c.label.toLowerCase()}`}>
                    <div className="w-10 h-10 rounded-full bg-[#C5A880]/10 flex items-center justify-center mb-3">
                      <c.icon className="text-[#C5A880]" size={20} />
                    </div>
                    <p className="text-[#EADBC8]/50 text-xs uppercase tracking-wider mb-1">{c.label}</p>
                    <p className="text-[#EADBC8] group-hover:text-[#C5A880] transition-colors break-all">{c.value}</p>
                  </a>
                ))}
              </div>

              <Card className="bg-[#2A2420] border-[#C5A880]/20">
                <CardContent className="p-6 space-y-5">
                  <div className="flex items-start gap-4">
                    <MapPin className="text-[#C5A880] flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <h3 className="text-[#EADBC8] font-semibold mb-1">Service area</h3>
                      <p className="text-[#EADBC8]/60 text-sm">Mobile service based in Cranbourne, Victoria — serving {SERVICE_AREAS.slice(0, 5).join(', ')} and greater Melbourne (subject to availability and travel fees).</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="text-[#C5A880] flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <h3 className="text-[#EADBC8] font-semibold mb-1">Response time</h3>
                      <p className="text-[#EADBC8]/60 text-sm">Enquiries are normally answered within two business days.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Star className="text-[#C5A880] flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <h3 className="text-[#EADBC8] font-semibold mb-1">Loved working with us?</h3>
                      <a href={review} target="_blank" rel="noopener noreferrer" className="text-[#C5A880] hover:underline text-sm" data-testid="contact-google-review">Leave a Google review</a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex">
              <Card className="bg-gradient-to-br from-[#2A2420] to-[#1F1A17] border-[#C5A880]/20 w-full">
                <CardContent className="p-8 flex flex-col justify-center h-full text-center">
                  <div className="w-14 h-14 rounded-full bg-[#C5A880]/10 flex items-center justify-center mx-auto mb-5">
                    <MessageSquare className="text-[#C5A880]" size={26} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-3">{t('card_title', 'Ready to check your date?')}</h2>
                  <p className="text-[#EADBC8]/70 mb-8">
                    {t('card_subtitle', 'The quickest way to get a personalised quote is the enquiry form — tell me the date, venue and celebration and I\u2019ll confirm availability.')}
                  </p>
                  <Link to="/quote"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-8 py-6 w-full sm:w-auto sm:mx-auto">{t('card_button', 'Check Your Event Date')}</Button></Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
